import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  AdminUserRow,
  createFittingTag,
  createGrant,
  deleteFittingTag,
  deleteGrant,
  fetchAdminFittingDetail,
  fetchAdminSettings,
  fetchAllFittingTags,
  fetchAllFittings,
  fetchAllUsers,
  fetchCorpMembers,
  fetchFittingTagTypes,
  fetchGrants,
  fetchStaticCandidates,
  fetchVaultDeletions,
  fetchOps,
  promoteStatic,
  resolveCharacterName,
  resolveOrgName,
  startAdminReauth,
  updateAdminSettings,
} from '../api/admin';
import {
  Webhook,
  WebhookEventOption,
  WebhookScopeType,
  createWebhook,
  deleteWebhook,
  fetchWebhookEvents,
  fetchWebhooks,
  testWebhook,
  updateWebhook,
} from '../api/webhooks';
import { fetchMaps } from '../api/maps';
import { timeAgo } from './system/KillRow';
import ConsolePanel from '../components/ConsolePanel';
import { FittingStatsPanel } from './Fittings';
import PanelHeader from '../components/PanelHeader';
import ScanFrame from '../components/ScanFrame';
import StatusBadge from '../components/StatusBadge';
import { useAuth } from '../services/AuthContext';
import { formatServerDateTime } from '../services/time';
import { C, FONT, frameSx, hexToRgba, lcarsLabelSx, revealSx } from '../theme/tokens';

type SubjectType = 'character' | 'corp' | 'alliance';
type Role = 'admin' | 'member' | 'viewer' | 'director';

// What resolvePermissions (src/services/permissions.ts) actually does with
// each role. 'viewer' spent a long time as a synonym for 'member' — the ACL
// accepted it and granted writes anyway. It is now the real read-only tier,
// enforced by requireEditor() on every mutating route, so this copy states
// the restriction because the restriction now exists.
const ROLE_COPY: Record<Role, string> = {
  admin: 'Full admin console access — grants, users, workers, webhooks, audit log — plus everything member gives.',
  member: 'Full app access, and can edit: deposit systems, write notes, log connections, manage vaults.',
  viewer:
    'Read-only. Sees every vault, note and connection they have access to, and can star systems for themselves — but cannot change corp data. The Data Vault opens in Read mode with no editing controls.',
  director:
    'Webhook settings only — nothing else in the admin console. Locked to configuring this corp/alliance’s own Discord webhooks; cannot grant roles, manage users, or touch another org’s webhooks.',
};

/** Danger-red ScanFrame signals elevated access — the one screen in the
 * app that isn't wormhole/cyan-branded, now using the same corner-bracket
 * + dot/label idiom every other hero section in the app carries, just in
 * red. Tabs: Permission Grants, Corp Roster Search (Phase 3), Static
 * Promotion (Phase 4's "promote observed connection" flow). */
const AdminPanel: React.FC = () => {
  const { me } = useAuth();
  const [searchParams] = useSearchParams();
  // The vault's "Create webhook for this vault" shortcut deep-links here
  // with ?tab=6&vault=<id> — read once on mount so it doesn't fight the
  // Tabs component's own state on every render.
  const [tab, setTab] = useState(() => Number(searchParams.get('tab')) || 0);

  // A director grant unlocks only this one tab — no grants, users, workers,
  // fittings or audit log alongside it. Gate.tsx/NavBar already let a
  // director-only user reach /admin at all; this is what limits what they
  // find once here.
  if (!me.isAdmin && me.isDirector) {
    return (
      <Box sx={revealSx()}>
        <ScanFrame color={C.red} sx={{ mb: 3, display: 'block' }}>
          <PanelHeader label="Webhook Settings — Director Access" accent={C.red} dense />
        </ScanFrame>
        <WebhooksTab />
      </Box>
    );
  }

  return (
    <Box sx={revealSx()}>
      <ScanFrame color={C.red} sx={{ mb: 3, display: 'block' }}>
        <PanelHeader label="Admin Console — Elevated Access" accent={C.red} dense />
      </ScanFrame>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Permission Grants" />
        <Tab label="Corp Roster Search" />
        <Tab label="Static Promotion" />
        <Tab label="Users" />
        <Tab label="Fittings" />
        <Tab label="Workers" />
        <Tab label="Webhooks" />
        <Tab label="Audit Log" />
        <Tab label="Operations" />
      </Tabs>
      {tab === 0 && <PermissionGrantsTab />}
      {tab === 1 && <CorpRosterTab />}
      {tab === 2 && <StaticPromotionTab />}
      {tab === 3 && <UsersTab />}
      {tab === 4 && <FittingTagsTab />}
      {tab === 5 && <WorkersTab />}
      {tab === 6 && <WebhooksTab />}
      {tab === 7 && <AuditLogTab />}
      {tab === 8 && <OperationsTab />}
    </Box>
  );
};

/**
 * The two standing questions leadership had no answer to: who is actually
 * feeding the shared intel, and where is the tracking blind.
 *
 * Deliberately not a movement log. The presence data would support "where
 * has this character been", and showing that would turn an opt-in
 * convenience into surveillance of members' alts — a trust decision, not a
 * side effect of an admin screen. Contribution counts are things members
 * chose to SHARE with the corp; coverage is about whether the tool works.
 */
const OperationsTab: React.FC = () => {
  const [days, setDays] = useState(30);
  const ops = useQuery({ queryKey: ['admin', 'ops', days], queryFn: () => fetchOps(days) });

  if (ops.isLoading) return <CircularProgress size={22} sx={{ color: C.red }} />;
  // The shape check is not paranoia about our own endpoint: a 200 carrying
  // the wrong body — a proxy answering, a deploy where this route does not
  // exist yet — left `coverage` undefined, and `.filter` on it threw during
  // render, which in React takes down the WHOLE admin console rather than
  // this one tab. A white page is a much worse failure than the message
  // below, and it is the failure an admin hits when something upstream is
  // already wrong. Found by rendering this tab with no fixture behind it.
  const report = ops.data;
  const malformed = !report || !Array.isArray(report.coverage) || !Array.isArray(report.contributors);
  if (ops.isError || malformed) {
    return <Typography sx={{ color: C.gold }}>Operations data unavailable right now.</Typography>;
  }

  const blind = report.coverage.filter((c) => !c.canTrackLocation || c.authFailing);

  return (
    <Stack spacing={3}>
      <ConsolePanel title="Tracking Coverage" accent={blind.length > 0 ? C.gold : C.green}>
        <Typography sx={{ color: C.textDim, fontSize: '0.82rem', mb: 1.5 }}>
          A character can be linked without location scope, or have its token quietly expire — either way tracking
          never covers them and nobody finds out. {blind.length === 0
            ? 'Every linked character is currently trackable.'
            : `${blind.length} of ${report.coverage.length} characters are not being tracked.`}
        </Typography>
        <Stack spacing={0.5}>
          {blind.map((c) => (
            // Wraps, and the name's reserved width is a breakpoint rather
            // than a constant: 160px of it on a 390px screen left the badge
            // and the login stamp nowhere to go, and a non-wrapping row
            // pushed them off the side — 147px of horizontal page overflow,
            // the only page in the app with any. The name column still
            // lines up on a desktop, which is what the minWidth was for.
            <Stack
              key={c.eveCharacterId}
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ borderBottom: `1px solid ${C.borderSub}`, py: 0.5 }}
            >
              <Typography
                sx={{ fontFamily: FONT.mono, fontSize: '0.85rem', color: C.text, minWidth: { xs: 0, sm: 160 } }}
              >
                {c.name}
              </Typography>
              {/* Two different problems needing two different fixes: grant
                  the scope, or re-link the character. */}
              {!c.canTrackLocation && <StatusBadge label="No location scope" accent={C.gold} />}
              {c.authFailing && <StatusBadge label={`Auth failing (${c.refreshAttempts})`} accent={C.red} />}
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.72rem' }}>
                {c.lastLoginAt ? `last login ${formatServerDateTime(c.lastLoginAt)}` : 'never logged in'}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </ConsolePanel>

      <ConsolePanel title="Contributions" accent={C.cyan}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
          <Typography sx={{ color: C.textDim, fontSize: '0.82rem' }}>Window</Typography>
          {[7, 30, 90].map((d) => (
            <Chip
              key={d}
              label={`${d}d`}
              size="small"
              onClick={() => setDays(d)}
              sx={{
                cursor: 'pointer',
                fontFamily: FONT.mono,
                fontSize: '0.72rem',
                color: days === d ? C.bg : C.text,
                backgroundColor: days === d ? C.cyan : hexToRgba(C.cyan, 0.08),
              }}
            />
          ))}
        </Stack>
        {report.contributors.length === 0 ? (
          <Typography sx={{ color: C.textDim, fontSize: '0.82rem' }}>
            Nothing logged in the last {report.windowDays} days.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {report.contributors.map((c) => (
              // Same wrap as the coverage rows above: three badges plus a
              // name do not fit 390px on one line.
              <Stack
                key={c.eve_character_id}
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ borderBottom: `1px solid ${C.borderSub}`, py: 0.5 }}
              >
                <Typography
                  sx={{ fontFamily: FONT.mono, fontSize: '0.85rem', color: C.text, minWidth: { xs: 0, sm: 160 } }}
                >
                  {c.name}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {c.signatures > 0 && <StatusBadge label={`${c.signatures} sigs`} accent={C.cyan} dim />}
                {c.connections > 0 && <StatusBadge label={`${c.connections} connections`} accent={C.wormhole} dim />}
                {c.sightings > 0 && <StatusBadge label={`${c.sightings} sightings`} accent={C.gold} dim />}
              </Stack>
            ))}
          </Stack>
        )}
      </ConsolePanel>
    </Stack>
  );
};

const PermissionGrantsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: grants } = useQuery({ queryKey: ['admin', 'grants'], queryFn: fetchGrants });
  const [subjectType, setSubjectType] = useState<SubjectType>('character');
  const [name, setName] = useState('');
  const [resolved, setResolved] = useState<{ id: number; name: string } | null>(null);
  const [role, setRole] = useState<Role>('member');
  const [resolveError, setResolveError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'grants'] });

  const resolveMutation = useMutation({
    mutationFn: () => (subjectType === 'character' ? resolveCharacterName(name) : resolveOrgName(name)),
    onSuccess: (r) => {
      setResolved(r);
      setResolveError(null);
    },
    onError: () => setResolveError('No exact match found.'),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createGrant({ subjectType, subjectId: resolved!.id, subjectName: resolved!.name, role }),
    onSuccess: () => {
      setName('');
      setResolved(null);
      invalidate();
    },
  });

  const deleteMutation = useMutation({ mutationFn: (id: number) => deleteGrant(id), onSuccess: invalidate });

  return (
    <Stack spacing={3}>
      <Stack spacing={1} sx={{ maxWidth: 480 }}>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            select
            label="Type"
            value={subjectType}
            onChange={(e) => {
              setSubjectType(e.target.value as SubjectType);
              setResolved(null);
            }}
            sx={{ width: 130 }}
          >
            <MenuItem value="character">Character</MenuItem>
            <MenuItem value="corp">Corp</MenuItem>
            <MenuItem value="alliance">Alliance</MenuItem>
          </TextField>
          <TextField
            size="small"
            placeholder="Exact name or ID"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setResolved(null);
            }}
            sx={{ flex: 1 }}
          />
          <Button size="small" variant="outlined" onClick={() => resolveMutation.mutate()} disabled={!name.trim()}>
            Resolve
          </Button>
        </Stack>
        {resolveError && <Typography sx={{ color: C.red, fontSize: '0.8rem' }}>{resolveError}</Typography>}
        {resolved && (
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ color: C.text }}>
                &rarr; {resolved.name} (#{resolved.id})
              </Typography>
              <TextField size="small" select value={role} onChange={(e) => setRole(e.target.value as Role)} sx={{ width: 120 }}>
                <MenuItem value="admin">admin</MenuItem>
                <MenuItem value="member">member</MenuItem>
                <MenuItem value="viewer">viewer</MenuItem>
                <MenuItem value="director">director</MenuItem>
              </TextField>
              <Button size="small" variant="contained" color="secondary" onClick={() => createMutation.mutate()}>
                Grant
              </Button>
            </Stack>
            <Typography sx={{ color: C.textDim, fontSize: '0.72rem' }}>{ROLE_COPY[role]}</Typography>
          </Stack>
        )}
      </Stack>

      <Stack spacing={0.5}>
        {(grants ?? []).map((g) => (
          <Stack key={g.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ borderBottom: `1px solid ${C.borderSub}`, py: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.85rem', color: C.text }}>
                [{g.subject_type}] {g.subject_name ?? `#${g.subject_id}`} &mdash;{' '}
                <Tooltip title={ROLE_COPY[g.role as Role]}>
                  <span style={{ textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>{g.role}</span>
                </Tooltip>
              </Typography>
              {/* Self-service proof (Settings > Verify in-game Director role),
                  distinct from the admin-side corp-wide check on the Corp
                  Roster Search tab — either can confirm the same fact. */}
              {g.role === 'director' &&
                (g.esi_director_verified_at ? (
                  <Tooltip title={`Self-verified in-game Director role on ${new Date(g.esi_director_verified_at).toLocaleString()}`}>
                    <span>
                      <StatusBadge label="ESI verified" accent={C.gold} dim />
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title="This character has not run Settings > Verify in-game Director role.">
                    <span>
                      <StatusBadge label="unverified" accent={C.textDim} dim />
                    </span>
                  </Tooltip>
                ))}
            </Stack>
            <IconButton size="small" onClick={() => deleteMutation.mutate(g.id)} sx={{ color: C.textDim }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

const CorpRosterTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [corp, setCorp] = useState<{ id: number; name: string } | null>(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [notYourCorp, setNotYourCorp] = useState(false);

  const resolveMutation = useMutation({
    mutationFn: () => resolveOrgName(name),
    onSuccess: (r) => {
      setCorp(r);
      setNeedsReauth(false);
      setNotYourCorp(false);
    },
  });

  const membersQuery = useQuery({
    queryKey: ['admin', 'corp-members', corp?.id],
    queryFn: () => fetchCorpMembers(corp!.id),
    enabled: !!corp,
    retry: false,
  });

  // Shares the ['admin','grants'] cache key with PermissionGrantsTab — this
  // is what "does the in-game role match the app grant" cross-references
  // against.
  const { data: grants } = useQuery({ queryKey: ['admin', 'grants'], queryFn: fetchGrants });

  React.useEffect(() => {
    const err = membersQuery.error as { response?: { data?: { error?: string } } } | null;
    if (err?.response?.data?.error === 'needs_admin_reauth') setNeedsReauth(true);
    if (err?.response?.data?.error === 'not_your_corp') setNotYourCorp(true);
  }, [membersQuery.error]);

  const grantMutation = useMutation({
    mutationFn: (member: { id: number; name: string; role?: 'member' | 'director' }) =>
      createGrant({ subjectType: 'character', subjectId: member.id, subjectName: member.name, role: member.role ?? 'member' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'grants'] }),
  });

  const reauth = async () => {
    const { auth_url } = await startAdminReauth();
    window.location.href = auth_url;
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} sx={{ maxWidth: 480 }}>
        <TextField size="small" placeholder="Corp or alliance name" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" onClick={() => resolveMutation.mutate()} disabled={!name.trim()}>
          Search
        </Button>
      </Stack>

      {notYourCorp && (
        <Typography sx={{ color: C.red }}>ESI only allows listing your own corp&rsquo;s roster.</Typography>
      )}
      {needsReauth && (
        <Stack spacing={1}>
          <Typography sx={{ color: C.textDim }}>
            Corp roster access needs an extra scope grant on top of your normal login.
          </Typography>
          <Button size="small" variant="contained" color="secondary" onClick={reauth} sx={{ alignSelf: 'flex-start' }}>
            Connect corp roster access
          </Button>
        </Stack>
      )}

      {membersQuery.data && (
        <Stack spacing={0.5}>
          {!membersQuery.data.isRoleCheckAvailable && (
            <Typography sx={{ color: C.textDim, fontSize: '0.75rem' }}>
              In-game role data is unavailable — ESI only returns it when your own character holds a Director-level
              role in this corp. The member list below is still accurate.
            </Typography>
          )}
          {membersQuery.data.members.map((m) => {
            // The check this whole tab exists for: does the app-side
            // 'director' grant (an admin's own by-name judgment call) match
            // what ESI actually says about this member's in-game role?
            const hasDirectorGrant = (grants ?? []).some(
              (g) => g.subject_type === 'character' && g.subject_id === m.id && g.role === 'director',
            );
            const mismatch = m.isEsiDirector !== null && m.isEsiDirector !== hasDirectorGrant;
            return (
              <Stack
                key={m.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ borderBottom: `1px solid ${C.borderSub}`, py: 0.5 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.85rem', color: C.text }}>{m.name}</Typography>
                  {m.isEsiDirector && <StatusBadge label="Director (ESI)" accent={C.gold} />}
                  {mismatch && (
                    <Tooltip
                      title={
                        m.isEsiDirector
                          ? "Holds the in-game Director role but has no 'director' app grant."
                          : "Has a 'director' app grant but ESI shows no in-game Director role."
                      }
                    >
                      <span>
                        <StatusBadge label="mismatch" accent={C.red} dim />
                      </span>
                    </Tooltip>
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  {m.isEsiDirector && !hasDirectorGrant && (
                    <Button size="small" color="secondary" onClick={() => grantMutation.mutate({ ...m, role: 'director' })}>
                      Grant Director
                    </Button>
                  )}
                  <Button size="small" onClick={() => grantMutation.mutate(m)}>
                    Grant Member
                  </Button>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

const StaticPromotionTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: candidates, isLoading } = useQuery({
    queryKey: ['admin', 'static-candidates'],
    queryFn: fetchStaticCandidates,
  });

  const promoteMutation = useMutation({
    mutationFn: ({ solarSystemId, whTypeCode }: { solarSystemId: number; whTypeCode: string }) =>
      promoteStatic(solarSystemId, whTypeCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'static-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['intel'] });
    },
  });

  if (isLoading) return <Typography sx={{ color: C.textDim }}>Loading&hellip;</Typography>;

  const intro = (
    <Typography sx={{ color: C.textDim, fontSize: '0.8rem', maxWidth: 720 }}>
      A system&rsquo;s static wormhole connection is normally known from curated community data. When corp members
      log a connection type from a system that has none on file, it shows up here instead of silently going
      unrecorded. Promoting adds it as a static with source &ldquo;corp_observed&rdquo; — it then appears on that
      system&rsquo;s intel page like any curated static. This is a suggestion from your own corp&rsquo;s map data,
      not a confirmed fact — check the observer count below before promoting; one person re-logging the same
      connection several times is a much weaker signal than several different people independently seeing it.
    </Typography>
  );

  if (!candidates || candidates.length === 0) {
    return (
      <Stack spacing={1.5}>
        {intro}
        <Typography sx={{ color: C.textDim, fontSize: '0.8rem' }}>
          Nothing to promote right now — every repeatedly-logged connection already has a curated static on file.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      {intro}
      <Stack spacing={1}>
        {candidates.map((c) => {
          const confident = c.distinct_observers >= 3;
          const shaky = c.distinct_observers === 1;
          return (
            <Box
              key={`${c.solar_system_id}-${c.wh_type_code}`}
              sx={{ ...frameSx(confident ? C.cyan : C.gold), p: 1.5 }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.9rem', color: C.text, fontWeight: 600 }}>
                  {c.system_name}
                </Typography>
                <StatusBadge label={c.wh_type_code} accent={C.cyan} />
                <StatusBadge
                  label={`${c.distinct_observers} observer${c.distinct_observers === 1 ? '' : 's'}`}
                  accent={confident ? C.cyan : shaky ? C.gold : C.textDim}
                  dim={shaky}
                />
                <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.7rem' }}>
                  {c.observations}&times; logged &middot; last {timeAgo(c.last_observed_at)}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => promoteMutation.mutate({ solarSystemId: c.solar_system_id, whTypeCode: c.wh_type_code })}
                  disabled={promoteMutation.isPending}
                >
                  Promote
                </Button>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

const UsersTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ['admin', 'settings'], queryFn: fetchAdminSettings });
  // Wrapped, not passed by reference: react-query hands queryFn a context
  // object, which would land in the pagination params.
  const { data: users, isLoading } = useQuery({ queryKey: ['admin', 'users'], queryFn: () => fetchAllUsers() });

  const toggleMutation = useMutation({
    mutationFn: (adminOnlyLogin: boolean) => updateAdminSettings({ adminOnlyLogin }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });

  const grouped = new Map<number, AdminUserRow[]>();
  for (const u of users?.items ?? []) {
    if (!grouped.has(u.accountId)) grouped.set(u.accountId, []);
    grouped.get(u.accountId)!.push(u);
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5} sx={{ maxWidth: 560 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Switch
            checked={!!settings?.adminOnlyLogin}
            onChange={(e) => toggleMutation.mutate(e.target.checked)}
            disabled={!settings}
          />
          <Typography sx={{ color: C.text, fontWeight: 600 }}>Admin-only login (lockdown)</Typography>
        </Stack>
        <Typography sx={{ color: C.textDim, fontSize: '0.78rem' }}>
          Off: normal corp/alliance member gate applies, as today. On: only admin-listed characters can complete
          login — every other character, including existing corp/alliance members, is rejected at the SSO callback.
        </Typography>
      </Stack>

      {isLoading ? (
        <Typography sx={{ color: C.textDim }}>Loading&hellip;</Typography>
      ) : (
        <Stack spacing={2}>
          {/* The list is paged. Say so when it is cut, rather than letting a
              truncated page read as the whole roster. */}
          {users && users.total > users.items.length && (
            <Typography sx={{ color: C.gold, fontFamily: FONT.mono, fontSize: '0.75rem' }}>
              Showing {users.items.length} of {users.total} characters.
            </Typography>
          )}
          {Array.from(grouped.entries()).map(([accountId, rows]) => (
            <Stack key={accountId} spacing={0.5}>
              <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.7rem', textTransform: 'uppercase' }}>
                Account #{accountId}
              </Typography>
              {rows.map((u) => (
                <Stack
                  key={u.eveCharacterId}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ borderBottom: `1px solid ${C.borderSub}`, py: 0.5 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.85rem', color: C.text }}>{u.name}</Typography>
                    {u.isMain && <StatusBadge label="Main" accent={C.blue} />}
                    {u.isAdmin && <StatusBadge label="Admin" accent={C.red} />}
                  </Stack>
                  <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.75rem' }}>
                    {u.corpName ?? '—'} &middot; last login{' '}
                    {u.lastLoginAt ? formatServerDateTime(u.lastLoginAt) : 'never'}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const WH_CLASSES = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6'];
type Recommendation = 'minimum' | 'recommended';

/** The corp-wide view of "what to bring" curation.
 *
 * Tagging itself is no longer an admin action — it moved onto the fit, in
 * the Fittings page's dialog (components/FittingTagEditor), because the
 * people flying a doctrine are the ones who know what it is for. What stays
 * admin-only is here: every tag in the app on one screen, and the override
 * that removes somebody else's. Still promote-not-compute — no auto-scoring
 * against a fitting's own stats (that comparison is left to the viewer on
 * the site detail page, which shows both side by side). */
const FittingTagsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: fittings } = useQuery({ queryKey: ['admin', 'fittings-all'], queryFn: () => fetchAllFittings() });
  const { data: tags } = useQuery({ queryKey: ['admin', 'fitting-tags'], queryFn: fetchAllFittingTags });
  const { data: tagTypes } = useQuery({ queryKey: ['admin', 'fitting-tag-types'], queryFn: fetchFittingTagTypes });

  const [fittingId, setFittingId] = useState<number | ''>('');
  const [tagType, setTagType] = useState('wh_class');
  const [tagValue, setTagValue] = useState('');
  const [recommendation, setRecommendation] = useState<Recommendation>('recommended');

  // The full fit — slots, tank, DPS, Jita cost — for whichever one is
  // selected above. Previously the picker just held an id with nothing to
  // show for it: tagging meant typing a category and value against a name
  // in a dropdown, with no way to check "is this actually the C5 Loki I
  // meant" before hitting Tag.
  const { data: selectedFitting, isLoading: fittingLoading } = useQuery({
    queryKey: ['admin', 'fitting-detail', fittingId],
    queryFn: () => fetchAdminFittingDetail(Number(fittingId)),
    enabled: fittingId !== '',
  });

  // Built-ins first, then whatever else is already in use — so a corp that
  // starts tagging "budget" or "solo-friendly" sees those offered right back
  // to them instead of retyping a slightly different spelling each time.
  const knownTypes = Array.from(new Set(['wh_class', 'site', ...(tagTypes ?? [])]));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'fitting-tags'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'fitting-tag-types'] });
  };

  const createMutation = useMutation({
    mutationFn: () => createFittingTag({ fittingId: Number(fittingId), tagType: tagType.trim(), tagValue: tagValue.trim(), recommendation }),
    onSuccess: () => {
      setTagValue('');
      invalidate();
    },
  });
  const deleteMutation = useMutation({ mutationFn: (id: number) => deleteFittingTag(id), onSuccess: invalidate });

  const fittingName = (id: number) => {
    const f = fittings?.items.find((f) => f.id === id);
    return f ? `${f.name} (${f.ship_name})` : `#${id}`;
  };

  return (
    <Stack spacing={3}>
      <ConsolePanel title="Tag a Fitting" accent={C.cyan}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="flex-start">
            <TextField
              size="small"
              select
              label="Fitting"
              value={fittingId}
              onChange={(e) => setFittingId(Number(e.target.value))}
              sx={{ minWidth: 260, flex: 1 }}
            >
              {(fittings?.items ?? []).map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name} ({f.ship_name})
                </MenuItem>
              ))}
            </TextField>
            {fittings && fittings.total > fittings.items.length && (
              <Typography sx={{ color: C.gold, fontFamily: FONT.mono, fontSize: '0.75rem', alignSelf: 'center' }}>
                {fittings.items.length} of {fittings.total} shown
              </Typography>
            )}
            <Autocomplete
              size="small"
              freeSolo
              options={knownTypes}
              value={tagType}
              onInputChange={(_e, v) => setTagType(v)}
              sx={{ width: 180 }}
              renderInput={(params) => <TextField {...params} label="Tag type" placeholder="wh_class, site, budget…" />}
            />
            {tagType === 'wh_class' ? (
              <TextField size="small" select label="Class" value={tagValue} onChange={(e) => setTagValue(e.target.value)} sx={{ width: 120 }}>
                {WH_CLASSES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField size="small" label="Value" value={tagValue} onChange={(e) => setTagValue(e.target.value)} sx={{ minWidth: 200, flex: 1 }} />
            )}
            <TextField
              size="small"
              select
              label="Level"
              value={recommendation}
              onChange={(e) => setRecommendation(e.target.value as Recommendation)}
              sx={{ width: 150 }}
            >
              <MenuItem value="recommended">recommended</MenuItem>
              <MenuItem value="minimum">minimum</MenuItem>
            </TextField>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              disabled={!fittingId || !tagType.trim() || !tagValue.trim()}
              onClick={() => createMutation.mutate()}
            >
              Tag
            </Button>
          </Stack>
          <Typography sx={{ color: C.textDim, fontSize: '0.72rem' }}>
            Tag type isn&rsquo;t limited to WH class or site — type any category (budget, solo, doctrine, whatever
            your corp finds useful) and it&rsquo;s remembered as a quick pick next time.
          </Typography>
        </Stack>
      </ConsolePanel>

      {/* The fit itself, right below the picker — same workbench/stats view
          the member-facing Fittings dialog uses, so an admin can actually
          see what "C5 Loki" means before tagging it rather than trusting
          the dropdown label alone. */}
      {fittingId !== '' && (
        <>
          {fittingLoading || !selectedFitting ? (
            <ConsolePanel title="Fitting" accent={C.blue}>
              <Typography sx={{ color: C.textDim }}>Loading&hellip;</Typography>
            </ConsolePanel>
          ) : (
            <FittingStatsPanel fitting={selectedFitting} />
          )}
        </>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(320px, 1fr))' },
          gap: 1.25,
        }}
      >
        {(tags ?? []).map((t) => (
          <Box key={t.id} sx={{ ...frameSx(C.cyan), p: 1.5 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: C.text, fontWeight: 600, fontSize: '0.85rem' }}>{fittingName(t.fitting_id)}</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  <StatusBadge label={t.tag_type} accent={C.blue} />
                  <StatusBadge label={t.tag_value} accent={C.cyan} />
                  <StatusBadge label={t.recommendation} accent={t.recommendation === 'recommended' ? C.green : C.gold} />
                </Stack>
                <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.65rem', mt: 0.75 }}>
                  tagged by {t.created_by_name ?? `#${t.created_by_character_id}`}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => deleteMutation.mutate(t.id)} sx={{ color: C.textDim }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        ))}
        {(tags ?? []).length === 0 && (
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.8rem' }}>No tags yet.</Typography>
        )}
      </Box>
    </Stack>
  );
};

/**
 * The settings the background workers read on every tick — location tracking
 * and the webhook kill switch. These live in `app_settings`, which the
 * pollers re-read each pass, so a change here takes effect without a
 * redeploy and without restarting anything.
 */
const WorkersTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ['admin', 'settings'], queryFn: fetchAdminSettings });
  const [seconds, setSeconds] = useState<string>('');

  const mutation = useMutation({
    mutationFn: updateAdminSettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });

  if (!settings) return <Typography sx={{ color: C.textDim }}>Loading&hellip;</Typography>;

  const pending = seconds === '' ? settings.trackingPollSeconds : Number(seconds);
  const secondsValid = Number.isFinite(pending) && pending >= settings.pollSecondsMin && pending <= settings.pollSecondsMax;

  return (
    <Stack spacing={3} sx={{ maxWidth: 620 }}>
      <ConsolePanel title="Location Tracking" accent={C.cyan}>
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Switch
              checked={settings.trackingEnabled}
              onChange={(e) => mutation.mutate({ trackingEnabled: e.target.checked })}
            />
            <Typography sx={{ color: C.text, fontWeight: 600 }}>Track J-space jumps</Typography>
          </Stack>
          <Typography sx={{ color: C.textDim, fontSize: '0.78rem' }}>
            Polls every linked character&rsquo;s location and records J&rarr;J hops into the Data Vault&rsquo;s tracked
            compartment. Uses the location scope every character already grants at login &mdash; no re-auth needed.
            K-space gate jumps are never recorded.
          </Typography>

          <Box>
            <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.68rem'), mb: 0.75 }}>Poll interval</Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                type="number"
                value={seconds === '' ? String(settings.trackingPollSeconds) : seconds}
                onChange={(e) => setSeconds(e.target.value)}
                error={seconds !== '' && !secondsValid}
                helperText={`${settings.pollSecondsMin}–${settings.pollSecondsMax} seconds`}
                sx={{ width: 160 }}
                FormHelperTextProps={{ sx: { fontFamily: FONT.mono, fontSize: '0.62rem', mx: 0 } }}
              />
              <Button
                size="small"
                variant="contained"
                color="secondary"
                disabled={!secondsValid || pending === settings.trackingPollSeconds}
                onClick={() => mutation.mutate({ trackingPollSeconds: pending })}
              >
                Apply
              </Button>
            </Stack>
            <Typography sx={{ color: C.textDim, fontSize: '0.72rem', mt: 0.75 }}>
              Faster means fresher connections and more ESI calls &mdash; one per character per interval. The floor
              matches ESI&rsquo;s own cache on the location endpoint, below which polling re-reads an unchanged value.
            </Typography>
          </Box>
        </Stack>
      </ConsolePanel>

      {/* The webhook kill switch moved to the Webhooks tab itself (Delivery
          Settings panel) — far more discoverable sitting next to the
          webhooks it controls than buried in here. */}

      <ConsolePanel title="EVE Scout Poller" accent={C.blue}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ color: C.textDim, fontSize: '0.78rem' }}>
            Live Thera/Turnur exits, polled every 5 minutes and shown on K-space intel.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusBadge
              label={`${settings.eveScoutStatus.connections} live`}
              accent={settings.eveScoutStatus.lastUpdated ? C.blue : C.textDim}
            />
            <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.7rem' }}>
              {settings.eveScoutStatus.lastUpdated
                ? `updated ${new Date(settings.eveScoutStatus.lastUpdated).toLocaleTimeString()}`
                : 'no poll yet'}
            </Typography>
          </Stack>
        </Stack>
      </ConsolePanel>
    </Stack>
  );
};

/** Read-only view of vault_deletion_log — vault deletion is the one
 * irreversible action in the app, so who did it and when needs to stay
 * visible somewhere, not just written and forgotten. */
const AuditLogTab: React.FC = () => {
  const { data: rows, isLoading } = useQuery({ queryKey: ['admin', 'vault-deletions'], queryFn: fetchVaultDeletions });

  if (isLoading) return <Typography sx={{ color: C.textDim }}>Loading&hellip;</Typography>;

  if (!rows || rows.length === 0) {
    return <Typography sx={{ color: C.textDim }}>No vault deletions logged yet.</Typography>;
  }

  return (
    <Stack spacing={0.5}>
      <Typography sx={{ color: C.textDim, mb: 1 }}>
        Every deleted vault, most recent first. Deleting a vault cascades every entry and connection it contained.
      </Typography>
      {rows.map((r) => (
        <Stack
          key={r.id}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderBottom: `1px solid ${C.borderSub}`, py: 0.5 }}
        >
          <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.85rem', color: C.text }}>
            &ldquo;{r.vault_name}&rdquo; &mdash; owned by {r.owner_name ?? `#${r.owner_character_id}`}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.75rem' }}>
              {formatServerDateTime(r.deleted_at)}
            </Typography>
            <StatusBadge
              label={r.deleted_by_admin_override ? `${r.deleted_by_name ?? `#${r.deleted_by_character_id}`} (admin)` : 'by owner'}
              accent={r.deleted_by_admin_override ? C.red : C.textDim}
            />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

/** Discord webhook configuration. URLs are write-only: the API returns a
 * masked stub, so changing one means pasting a new URL rather than editing
 * the old one in place. */
type WebhookUpdateBody = Parameters<typeof updateWebhook>[1];

const SCOPE_LABELS: Record<WebhookScopeType, string> = {
  global: 'Everyone',
  corp: 'Corp',
  alliance: 'Alliance',
  vault: 'Vault',
};

/**
 * Delivery health at a glance.
 *
 * 'dead' is the one that matters: Discord answered 401/403/404 repeatedly,
 * which means the webhook was deleted or its token regenerated. The server
 * auto-disables those, so without this badge the row would just sit there
 * switched off with no stated reason.
 */
const WebhookHealthBadge: React.FC<{ webhook: Webhook }> = ({ webhook }) => {
  if (webhook.health === 'untested') {
    return <StatusBadge label="Not yet fired" accent={C.textDim} dim />;
  }
  if (webhook.health === 'ok') {
    return (
      <Tooltip title={webhook.last_delivered_at ? `Last delivered ${formatServerDateTime(webhook.last_delivered_at)}` : ''}>
        <span>
          <StatusBadge label="Delivering" accent={C.green} />
        </span>
      </Tooltip>
    );
  }
  const dead = webhook.health === 'dead';
  return (
    <Tooltip
      title={
        dead
          ? `Discord says this webhook no longer exists — disabled after ${webhook.consecutive_failures} failures. Recreate it in Discord and paste the new URL. ${webhook.last_error ?? ''}`
          : `${webhook.consecutive_failures} failed attempt(s). ${webhook.last_error ?? ''}`
      }
    >
      <span>
        <StatusBadge label={dead ? 'Dead — recreate' : 'Failing'} accent={dead ? C.red : C.gold} />
      </span>
    </Tooltip>
  );
};

const WebhooksTab: React.FC = () => {
  const { me } = useAuth();
  // A director's scope is fixed to their own corp/alliance server-side
  // regardless of what's submitted (see routes/admin/webhooks.ts) — the UI
  // mirrors that here so a director never sees a Global/Vault option or an
  // org search box that would just be overridden on save.
  const isDirectorOnly = me.isDirector && !me.isAdmin;
  const hasAlliance = !!me.character?.allianceName;

  const queryClient = useQueryClient();
  const { data: hooks } = useQuery({ queryKey: ['admin', 'webhooks'], queryFn: fetchWebhooks });
  const { data: events } = useQuery({ queryKey: ['admin', 'webhook-events'], queryFn: fetchWebhookEvents });
  const { data: vaults } = useQuery({ queryKey: ['maps'], queryFn: fetchMaps, enabled: !isDirectorOnly });
  // The global kill switch lives in app_settings, admin-only (a director's
  // reach stops at their own org's webhooks, never the whole app's delivery)
  // — shares the ['admin','settings'] cache key with WorkersTab.
  const { data: settings } = useQuery({ queryKey: ['admin', 'settings'], queryFn: fetchAdminSettings, enabled: me.isAdmin });
  const settingsMutation = useMutation({
    mutationFn: updateAdminSettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });

  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [mention, setMention] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [testResult, setTestResult] = useState<{ id: number; ok: boolean } | null>(null);
  const [listFilter, setListFilter] = useState('');
  const filteredHooks = (hooks ?? []).filter((h) => {
    const q = listFilter.trim().toLowerCase();
    if (!q) return true;
    return h.label.toLowerCase().includes(q) || (h.scope_name ?? '').toLowerCase().includes(q);
  });

  // VaultSummary's "Create webhook for this vault" shortcut deep-links here
  // with ?vault=<id> — only an admin can act on it (a director never gets a
  // vault scope option at all), so it's ignored otherwise.
  const [searchParams] = useSearchParams();
  const deepLinkedVaultId = !isDirectorOnly ? Number(searchParams.get('vault')) || null : null;

  const [scopeType, setScopeType] = useState<WebhookScopeType>(
    deepLinkedVaultId ? 'vault' : isDirectorOnly ? 'corp' : 'global',
  );
  const [orgQuery, setOrgQuery] = useState('');
  const [resolvedOrg, setResolvedOrg] = useState<{ id: number; name: string } | null>(null);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [vaultId, setVaultId] = useState<number | ''>(deepLinkedVaultId ?? '');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'webhooks'] });

  const resetScope = () => {
    setScopeType(isDirectorOnly ? 'corp' : 'global');
    setOrgQuery('');
    setResolvedOrg(null);
    setOrgError(null);
    setVaultId('');
  };

  const resolveOrgMutation = useMutation({
    mutationFn: () => resolveOrgName(orgQuery),
    onSuccess: (r) => {
      setResolvedOrg(r);
      setOrgError(null);
    },
    onError: () => setOrgError('No exact match found.'),
  });

  const scopeFields = (): { scope_type: WebhookScopeType; scope_id: number | null; scope_name: string | null } => {
    // The server pins a director's scope_id/scope_name to their own
    // corp/alliance no matter what's sent (routes/admin/webhooks.ts) — only
    // scope_type (corp vs alliance) is this UI's to choose.
    if (isDirectorOnly && (scopeType === 'corp' || scopeType === 'alliance')) {
      return {
        scope_type: scopeType,
        scope_id: null,
        scope_name: scopeType === 'alliance' ? (me.character?.allianceName ?? null) : (me.character?.corpName ?? null),
      };
    }
    if (scopeType === 'corp' || scopeType === 'alliance') {
      return { scope_type: scopeType, scope_id: resolvedOrg?.id ?? null, scope_name: resolvedOrg?.name ?? null };
    }
    if (scopeType === 'vault') {
      const vault = vaults?.find((v) => v.id === vaultId);
      return { scope_type: 'vault', scope_id: vaultId === '' ? null : vaultId, scope_name: vault?.name ?? null };
    }
    return { scope_type: 'global', scope_id: null, scope_name: null };
  };

  const scopeIncomplete = isDirectorOnly
    ? false
    : (scopeType === 'corp' || scopeType === 'alliance') && !resolvedOrg
      ? true
      : scopeType === 'vault' && vaultId === '';

  const createMutation = useMutation({
    mutationFn: () => createWebhook({ label, url, events: selected, mention: mention.trim() || null, ...scopeFields() }),
    onSuccess: () => {
      setLabel('');
      setUrl('');
      setMention('');
      setSelected([]);
      resetScope();
      invalidate();
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: WebhookUpdateBody }) => updateWebhook(id, body),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({ mutationFn: deleteWebhook, onSuccess: invalidate });
  const testMutation = useMutation({
    mutationFn: testWebhook,
    onSuccess: (_d, id) => setTestResult({ id, ok: true }),
    onError: (_e, id) => setTestResult({ id, ok: false }),
  });

  const looksLikeDiscord = /^https:\/\/(discord|discordapp)\.com\/api\/webhooks\/\d+\/\S+/.test(url.trim());

  return (
    <Box
      sx={{
        display: 'grid',
        // A single form column never needed 720px, and everything to its
        // right sat empty on anything wider than a laptop. The configured
        // list gets that space now — 1fr on desktop, stacked below the form
        // (not beside it) on anything narrower than a tablet.
        gridTemplateColumns: { xs: '1fr', lg: '440px 1fr' },
        gap: 3,
        alignItems: 'start',
      }}
    >
      <Stack spacing={3}>
        {me.isAdmin && settings && (
          <ConsolePanel title="Delivery Settings" accent={C.gold}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Switch
                checked={settings.webhooksEnabled}
                onChange={(e) => settingsMutation.mutate({ webhooksEnabled: e.target.checked })}
              />
              <Typography sx={{ color: C.text, fontWeight: 600 }}>Webhook delivery enabled</Typography>
            </Stack>
            <Typography sx={{ color: C.textDim, fontSize: '0.78rem', mt: 1 }}>
              Global kill switch. Off suppresses every configured webhook below without deleting any of them — use
              it to silence alerts during a deployment or an op, rather than removing URLs you&rsquo;ll have to
              paste back.
            </Typography>
          </ConsolePanel>
        )}

        <ConsolePanel title="Add Webhook" accent={C.cyan}>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Label"
            placeholder="e.g. #wh-intel"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <TextField
            size="small"
            label="Discord webhook URL"
            placeholder="https://discord.com/api/webhooks/…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={url.trim().length > 0 && !looksLikeDiscord}
            helperText={
              url.trim().length > 0 && !looksLikeDiscord
                ? "That doesn't look like a Discord webhook URL."
                : 'Stored write-only — it is never shown again after saving.'
            }
            FormHelperTextProps={{ sx: { fontFamily: FONT.mono, fontSize: '0.62rem', mx: 0 } }}
          />
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={lcarsLabelSx(C.textDim, '0.68rem')}>1. Fires on these events</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={() => setSelected((events ?? []).map((ev) => ev.key))}
                  disabled={!events || selected.length === events.length}
                  sx={{ fontSize: '0.68rem', minWidth: 0, p: 0 }}
                >
                  All
                </Button>
                <Button
                  size="small"
                  onClick={() => setSelected([])}
                  disabled={selected.length === 0}
                  sx={{ fontSize: '0.68rem', minWidth: 0, p: 0 }}
                >
                  None
                </Button>
              </Stack>
            </Stack>
            <Stack>
              {(events ?? []).map((ev) => (
                <FormControlLabel
                  key={ev.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.includes(ev.key)}
                      onChange={(e) =>
                        setSelected((prev) => (e.target.checked ? [...prev, ev.key] : prev.filter((k) => k !== ev.key)))
                      }
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.82rem', color: C.text }}>{ev.label}</Typography>}
                />
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.68rem'), mb: 0.5 }}>
              2. AND only when it&rsquo;s about
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                select
                value={scopeType}
                onChange={(e) => {
                  setScopeType(e.target.value as WebhookScopeType);
                  setOrgQuery('');
                  setResolvedOrg(null);
                  setOrgError(null);
                  setVaultId('');
                }}
                sx={{ width: 140 }}
              >
                {(isDirectorOnly
                  ? (['corp', 'alliance'] as WebhookScopeType[]).filter((t) => t === 'corp' || hasAlliance)
                  : (Object.keys(SCOPE_LABELS) as WebhookScopeType[])
                ).map((t) => (
                  <MenuItem key={t} value={t}>
                    {SCOPE_LABELS[t]}
                  </MenuItem>
                ))}
              </TextField>
              {isDirectorOnly && (scopeType === 'corp' || scopeType === 'alliance') && (
                // Locked to the director's own org — never a search box, since
                // the server ignores whatever scope_id/name a director sends
                // and substitutes their real corp/alliance anyway.
                <StatusBadge
                  label={(scopeType === 'alliance' ? me.character?.allianceName : me.character?.corpName) ?? '—'}
                  accent={C.cyan}
                />
              )}
              {!isDirectorOnly && (scopeType === 'corp' || scopeType === 'alliance') && !resolvedOrg && (
                <>
                  <TextField
                    size="small"
                    placeholder={scopeType === 'corp' ? 'Corp name or ID' : 'Alliance name or ID'}
                    value={orgQuery}
                    onChange={(e) => setOrgQuery(e.target.value)}
                    sx={{ flex: 1, minWidth: 180 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!orgQuery.trim()}
                    onClick={() => resolveOrgMutation.mutate()}
                  >
                    Resolve
                  </Button>
                </>
              )}
              {!isDirectorOnly && resolvedOrg && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <StatusBadge label={resolvedOrg.name} accent={C.cyan} />
                  <Button size="small" onClick={() => setResolvedOrg(null)} sx={{ fontSize: '0.68rem' }}>
                    change
                  </Button>
                </Stack>
              )}
              {!isDirectorOnly && scopeType === 'vault' && (
                <TextField
                  size="small"
                  select
                  value={vaultId}
                  onChange={(e) => setVaultId(Number(e.target.value))}
                  sx={{ minWidth: 200 }}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    Choose a vault&hellip;
                  </MenuItem>
                  {(vaults ?? []).map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Stack>
            {orgError && (
              <Typography sx={{ color: C.red, fontSize: '0.72rem', mt: 0.5 }}>{orgError}</Typography>
            )}
            <Typography sx={{ color: C.textDim, fontSize: '0.72rem', mt: 0.5 }}>
              {scopeType === 'global'
                ? 'No filter — every event checked above fires here, for any corp, alliance, or vault.'
                : `A second filter on top of step 1's events, not a separate trigger — an event above only reaches this webhook when it also belongs to the ${SCOPE_LABELS[scopeType].toLowerCase()} chosen here (e.g. a kill in a system tracked in that vault).`}
            </Typography>
            {scopeType !== 'global' && selected.includes('skyhook_raidable') && (
              <Typography sx={{ color: C.gold, fontSize: '0.72rem', mt: 0.5 }}>
                Heads up: skyhook alerts have no corp/alliance/vault of their own, so with this scope they will never
                actually fire — pick Everyone if you want skyhook alerts on this webhook.
              </Typography>
            )}
          </Box>
          <Box>
            <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.68rem'), mb: 0.5 }}>3. Ping (optional)</Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="@here, @everyone, or a role mention pasted from Discord"
              value={mention}
              onChange={(e) => setMention(e.target.value)}
            />
            <Typography sx={{ color: C.textDim, fontSize: '0.72rem', mt: 0.5 }}>
              Sent as its own line above the embed. Leave blank for a quiet post that only lights up the channel,
              not anyone in it.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            disabled={!label.trim() || !looksLikeDiscord || selected.length === 0 || scopeIncomplete || createMutation.isPending}
            onClick={() => createMutation.mutate()}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add webhook
          </Button>
          {createMutation.isError && (
            <Typography sx={{ color: C.red, fontSize: '0.75rem' }}>Couldn&rsquo;t save that webhook.</Typography>
          )}
        </Stack>
      </ConsolePanel>
      </Stack>

      <Stack spacing={1.5}>
        {(hooks ?? []).length > 4 && (
          <TextField
            size="small"
            placeholder="Filter by label or scope&hellip;"
            value={listFilter}
            onChange={(e) => setListFilter(e.target.value)}
            sx={{ maxWidth: 320 }}
          />
        )}
        {(hooks ?? []).length === 0 && (
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.8rem' }}>
            No webhooks configured.
          </Typography>
        )}
        {(hooks ?? []).length > 0 && filteredHooks.length === 0 && (
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.8rem' }}>
            No webhook matches &ldquo;{listFilter}&rdquo;.
          </Typography>
        )}
        {filteredHooks.map((h) => (
          <WebhookCard
            key={h.id}
            webhook={h}
            events={events ?? []}
            onUpdate={(body) => updateMutation.mutate({ id: h.id, body })}
            onDelete={() => deleteMutation.mutate(h.id)}
            onTest={() => testMutation.mutate(h.id)}
            testPending={testMutation.isPending}
            testResult={testResult?.id === h.id ? testResult.ok : undefined}
          />
        ))}
      </Stack>
    </Box>
  );
};

/** One configured webhook: enable switch, scope/summary at rest, and an
 * inline edit mode for the fields that used to require delete-and-recreate
 * (label, a replacement URL, the ping) — events and delete stay one click,
 * same as before. Its own component rather than inlined in the list `.map`
 * so the edit-mode draft fields (label/url/mention) can be local state
 * without threading a per-id map through WebhooksTab. */
const WebhookCard: React.FC<{
  webhook: Webhook;
  events: WebhookEventOption[];
  onUpdate: (body: WebhookUpdateBody) => void;
  onDelete: () => void;
  onTest: () => void;
  testPending: boolean;
  testResult?: boolean;
}> = ({ webhook: h, events, onUpdate, onDelete, onTest, testPending, testResult }) => {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(h.label);
  const [url, setUrl] = useState('');
  const [mention, setMention] = useState(h.mention ?? '');

  const urlLooksValid = url.trim() === '' || /^https:\/\/(discord|discordapp)\.com\/api\/webhooks\/\d+\/\S+/.test(url.trim());

  const startEditing = () => {
    setLabel(h.label);
    setUrl('');
    setMention(h.mention ?? '');
    setEditing(true);
  };

  const save = () => {
    const body: WebhookUpdateBody = {};
    if (label.trim() && label !== h.label) body.label = label.trim();
    if (url.trim()) body.url = url.trim();
    const nextMention = mention.trim() || null;
    if (nextMention !== h.mention) body.mention = nextMention;
    if (Object.keys(body).length > 0) onUpdate(body);
    setEditing(false);
  };

  return (
    <Box sx={{ ...frameSx(C.cyan), p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Switch size="small" checked={h.enabled} onChange={(e) => onUpdate({ enabled: e.target.checked })} />
        {editing ? (
          <Stack spacing={1} sx={{ flex: '1 1 320px', minWidth: 260 }}>
            <TextField size="small" label="Label" value={label} onChange={(e) => setLabel(e.target.value)} />
            <TextField
              size="small"
              label="Replace URL"
              placeholder="Paste a new Discord webhook URL to change it — leave blank to keep the current one"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              error={!urlLooksValid}
              FormHelperTextProps={{ sx: { fontFamily: FONT.mono, fontSize: '0.62rem', mx: 0 } }}
            />
            <TextField
              size="small"
              label="Mention"
              placeholder="@here, @everyone, or a role mention"
              value={mention}
              onChange={(e) => setMention(e.target.value)}
            />
          </Stack>
        ) : (
          <>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: C.text, fontWeight: 600, fontSize: '0.9rem' }}>{h.label}</Typography>
              <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.68rem' }}>{h.urlMasked}</Typography>
              {h.mention && (
                <Typography sx={{ color: C.gold, fontFamily: FONT.mono, fontSize: '0.68rem' }}>{h.mention}</Typography>
              )}
            </Box>
            <StatusBadge
              label={h.scope_type === 'global' ? 'Everyone' : `${SCOPE_LABELS[h.scope_type]}: ${h.scope_name ?? `#${h.scope_id}`}`}
              accent={h.scope_type === 'global' ? C.textDim : C.blue}
              dim={h.scope_type === 'global'}
            />
            {/* Whether this hook can still deliver. Before this the only way
                to find out was to press Send test on every row, one at a
                time — a webhook deleted in Discord looked identical to a
                working one and just silently swallowed every alert. */}
            <WebhookHealthBadge webhook={h} />
          </>
        )}
        <Box sx={{ flex: 1 }} />
        {editing ? (
          <>
            <Button size="small" variant="contained" color="secondary" disabled={!urlLooksValid} onClick={save}>
              Save
            </Button>
            <Button size="small" onClick={() => setEditing(false)} sx={{ color: C.textDim }}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button size="small" onClick={startEditing} sx={{ fontSize: '0.72rem' }}>
              Edit
            </Button>
            <Button size="small" onClick={onTest} disabled={testPending} sx={{ color: C.cyan, fontSize: '0.72rem' }}>
              Send test
            </Button>
            <IconButton size="small" onClick={onDelete} sx={{ color: C.textDim }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>

      {!editing && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
          {events.map((ev) => {
            const on = h.events.includes(ev.key);
            return (
              <Box
                key={ev.key}
                onClick={() => onUpdate({ events: on ? h.events.filter((k) => k !== ev.key) : [...h.events, ev.key] })}
                sx={{ cursor: 'pointer' }}
              >
                <StatusBadge label={ev.label} accent={on ? C.cyan : C.textDim} dim={!on} />
              </Box>
            );
          })}
        </Stack>
      )}

      {testResult !== undefined && (
        <Typography sx={{ color: testResult ? C.green : C.red, fontSize: '0.74rem', mt: 1 }}>
          {testResult ? 'Test message delivered.' : 'Delivery failed — check the URL is still valid.'}
        </Typography>
      )}
    </Box>
  );
};

export default AdminPanel;
